import { useEffect, useRef, useState } from "react";
import { UploadCloud, FileAudio, X } from "lucide-react";
import { toast } from "sonner";

const EXT = ["mp3", "wav", "m4a", "aac", "amr"];
const MAX = 10 * 1024 * 1024;

export const AudioDropzone = ({ file, onChange }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!file) { setUrl(null); return undefined; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => { URL.revokeObjectURL(u); };
  }, [file]);

  const pick = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!EXT.includes(ext)) { toast.error("Only .mp3, .wav, .m4a, .aac or .amr files are allowed"); return; }
    if (f.size > MAX) { toast.error("Audio file must be 10MB or smaller"); return; }
    onChange(f);
  };

  if (file) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="audio-preview">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-900 text-amber-300 grid place-items-center"><FileAudio className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-emerald-950" data-testid="audio-file-name">{file.name}</div>
            <div className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB · will be transcribed by AI</div>
          </div>
          <button type="button" data-testid="audio-remove-button" onClick={() => onChange(null)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-red-100 text-slate-500 hover:text-red-600 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        {url && !file.name.toLowerCase().endsWith(".amr") && <audio controls src={url} className="w-full h-10" data-testid="audio-preview-player" />}
        {file.name.toLowerCase().endsWith(".amr") && <p className="text-xs text-slate-500" data-testid="audio-amr-note">AMR files can't be previewed in the browser. It will be converted and playable after you submit.</p>}
      </div>
    );
  }

  return (
    <div
      data-testid="audio-dropzone"
      onClick={() => input.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
        drag ? "border-amber-500 bg-amber-50" : "border-emerald-900/20 bg-white/60 hover:border-emerald-600 hover:bg-white"
      }`}
    >
      <UploadCloud className="mx-auto h-8 w-8 text-emerald-700" />
      <div className="mt-2 text-sm font-semibold text-emerald-950">Drop guest voice recording here or <span className="text-amber-700 underline underline-offset-4">browse</span></div>
      <div className="mt-1 text-xs text-slate-500">MP3, WAV, M4A, AAC, AMR · up to 10MB</div>
      <input
        ref={input}
        type="file"
        accept=".mp3,.wav,.m4a,.aac,.amr,audio/*"
        className="hidden"
        data-testid="audio-dropzone-input"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
};
